import {lazy, Suspense, useEffect, useState} from 'react';
import {EventType, RedirectRequest} from '@azure/msal-browser';
import {AuthenticatedTemplate, useMsal} from '@azure/msal-react';

import {createHashRouter, Navigate, RouterProvider} from "react-router-dom";
import {config} from "./config.ts";
import {useRecoilValue} from "recoil";
import {userAccountInfoAtom} from "./state/users.ts";
import {useAuthActions} from "./actions/auth.ts";
import {LazyMainPage} from "./pages/MainPage.tsx";
import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import {isAdmin} from "./utils.ts";

// import axios from "axios";
// axios.interceptors.request.use(request => {
//   console.log('Starting Request', JSON.stringify(request, null, 2))
//   return request
// })

// axios.interceptors.response.use(response => {
//   console.log('Response:', JSON.stringify(response, null, 2))
//   return response
// })

const queryClient = new QueryClient()
const App = () => {
  const authActions = useAuthActions()
  const accountInfo = useRecoilValue(userAccountInfoAtom);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const { instance, accounts } = useMsal();

  const loginRequest: RedirectRequest = {
    scopes: config.scopes,
  }

  useEffect(() => {

    instance.addEventCallback((event: any) => {
      // set active account after redirect
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload.account) {
        const account = event.payload.account;
        instance.setActiveAccount(account);
      }
    });

    if (!accounts.length) {
      signIn();
    }

    const activeAccount = instance.getActiveAccount();
    if (activeAccount) {
        authActions.getUserInfo(isAuthenticated)
    }
    // eslint-disable-next-line
  }, [accounts])

  const logout = () => {
    instance.logoutRedirect()
  }

  const handleResponse = async (response: any) => {
    if (response !== null) {
      setIsAuthenticated(!!response.account);
      // Display signed-in user content, call API, etc.
    } else {
      // In case multiple accounts exist, you can select
      const currentAccounts = instance.getAllAccounts();

      if (currentAccounts.length === 0) {
        // no accounts signed-in, attempt to sign a user in
        await instance.loginRedirect(loginRequest)
      } else if (currentAccounts.length > 1) {
        // Add choose account code here
        console.warn('Multiple accounts detected.');
      } else if (currentAccounts.length === 1) {
        setIsAuthenticated(!!currentAccounts[0].homeAccountId);
      }
    }
  }

  const signIn = async () => {
    await instance.handleRedirectPromise()
        .then(handleResponse)
  }

  const MainPage = lazy(() => import("./pages/MainPage.tsx"))

  const UserListPage = lazy(() => import("./pages/UsersListPage.tsx"))
  const UserDetailPage = lazy(() => import("./pages/UserDetailPage.tsx"))

  const GroupsListPage = lazy(() => import("./pages/GroupsListPage.tsx"))
  const GroupDetailPage = lazy(() => import("./pages/GroupDetailPage.tsx"))

  const ApplicationsListPage = lazy(() => import("./pages/ApplicationsListPage.tsx"))
  const EmptyApplicationPage = lazy(() => import("./pages/EmptyApplicationPage.tsx"))
  const ApplicationDetailPage = lazy(() => import("./pages/ApplicationDetailPage.tsx"))

  const DataLimitationsListPage = lazy(() => import("./pages/DataLimitationsListPage.tsx"))
  const EmptyDataLimitationPage = lazy(() => import("./pages/EmptyDataLimitationPage.tsx"))
  const DataLimitationsDetailPage = lazy(() => import("./pages/DataLimitationsDetailPage.tsx"))

  const NotEnoughPrivilegesPage = lazy(() => import("./pages/NotEnoughPrivilegesPage.tsx"))

  const routes = [
      {id: "root", path: "/", element: <MainPage logout={logout} accountInfo={accountInfo} />, children:[
              {id: "UserList", loader: () => true, path: "", element: <UserListPage />},
              {id: "UserDetail",loader: () => true, path: "users/:id", element: <UserDetailPage accountInfo={accountInfo} />},
              {id: "GroupList",loader: () => true, path: "groups", element: <GroupsListPage accountInfo={accountInfo} />},
              {id: "GroupDetail", loader: () => true, path: "groups/:id", element: <GroupDetailPage accountInfo={accountInfo} />},
              !isAdmin(accountInfo) ? {id: "RedirectApplications", path: "*", loader: () => true, element: <Navigate to="/" replace />} :
                  {id: "Applications", loader: () => true, path: "applications", element: <ApplicationsListPage />, children:[
                      {id: "ApplicationEmpty", loader: () => true, path: "", element: <EmptyApplicationPage />},
                      {id: "ApplicationDetail", loader: () => true, path: ":id", element: <ApplicationDetailPage />},
                  ]
              },
              !isAdmin(accountInfo) ? {id: "RedirectDataLimitations", path: "*", loader: () => true, element: <Navigate to="/" replace />}  :
                  {id: "DataLimitations", loader: () => true, path: "data-limitations", element: <DataLimitationsListPage />, children:[
                      {id: "DataLimitationEmpty", loader: () => true, path: "", element: <EmptyDataLimitationPage />},
                      {id: "DataLimitationDetail", loader: () => true, path: ":id", element: <DataLimitationsDetailPage />},
                  ]
              },
          ]},
      {id: "NotEnoughPrivileges", path: "not-enough-privileges", loader: () => true, element: <NotEnoughPrivilegesPage logout={logout} accountInfo={accountInfo} />},
      {id: "Redirect", path: "*", loader: () => true, element: <Navigate to="/" replace />}
  ]
  const router = createHashRouter(routes)

  return (
      <AuthenticatedTemplate>
          <Suspense fallback={<LazyMainPage logout={logout} accountInfo={accountInfo} />}>
              <QueryClientProvider client={queryClient}>
                  <RouterProvider router={router} />
              </QueryClientProvider>
          </Suspense>
      </AuthenticatedTemplate>
  );
};

export default App;